import React, { useState } from 'react';
import axios from 'axios';

import { ADDON_LABELS, PLAN_BLURB, PLAN_OPTIONS } from '../../planVocab';

/**
 * A NEW STORE IS THE SAME PANEL, UNSAVED.
 *
 * Creating a store used to be a modal that asked for a name and an owner, and
 * then the plan was chosen somewhere else entirely. So the same two questions
 * lived in two places and one of them could only be answered afterwards.
 *
 * Here the draft IS the store panel: the identity fields are editable, the
 * plan is the same control it will be tomorrow, and the only thing a draft
 * adds is a save bar.
 *
 * The blocks that genuinely need a store id — add-ons, limits, exceptions,
 * the chat group — are SHOWN as dashed placeholders rather than hidden, so
 * the panel does not visibly change shape the moment you press Create.
 */

const TRIAL_DAYS = 30;

const IPlus = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const trialEnd = () => new Date(Date.now() + TRIAL_DAYS * 86400000);

export function StoreDraft({ onCancel, onCreated }) {
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [plan, setPlan] = useState(null);
  const [paid, setPaid] = useState('trial');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('owner');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const groupName = `${name.trim() || 'The store'} x Retail Circle`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');

    try {
      // ONE call. The store, its plan and the owner invitation are written
      // together — three separate calls meant "Create store" could half
      // succeed, leaving a store without the plan you had just chosen.
      const { data } = await axios.post('/api/admin/stores', {
        name: name.trim(),
        ...(website.trim() ? { website: website.trim() } : {}),
        plan,
        ...(plan ? { comped: paid === 'comped', trial_ends_at: paid === 'trial' ? trialEnd().toISOString().slice(0, 10) : null } : {}),
        ...(email.trim() ? { invite: { email: email.trim(), role } } : {}),
      });

      const store = data?.store || data;
      onCreated?.(store, data?.warning);
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Could not create the store.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="rc-sc__panel rc-sc__panel--draft" onSubmit={submit} aria-label="New store, not saved yet">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 14, borderBottom: '1px solid var(--sc-line)' }}>
        <span className="rc-sc__draft-tag"><IPlus /> New store · not saved yet</span>

        <div className="rc-sc__pair">
          <label className="rc-sc__field" style={{ flex: '999 1 280px' }}>
            <span>Store name <span className="rc-sc__req">*</span></span>
            <input
              className="rc-sc__input"
              type="text"
              value={name}
              required
              placeholder="Horology Partners"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="rc-sc__field" style={{ flex: '1 1 200px' }}>
            <span>Website <span className="rc-sc__opt">optional</span></span>
            <input
              className="rc-sc__input"
              type="url"
              value={website}
              placeholder="https://"
              onChange={(e) => setWebsite(e.target.value)}
            />
          </label>
        </div>
        <p className="rc-sc__hint">The chat group will be called “{groupName}”.</p>
      </div>

      <div className="rc-sc__band">
        <div className="rc-sc__band-head"><h4>Account</h4></div>

        <div className="rc-sc__pair">
          <fieldset className="rc-sc__seg" style={{ display: 'block' }}>
            <legend>Plan</legend>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                aria-pressed={plan === null}
                className={`rc-sc__opt-btn rc-sc__opt-btn--narrow ${plan === null ? 'rc-sc__opt-btn--none' : ''}`}
                onClick={() => setPlan(null)}
              >
                None
              </button>
              {PLAN_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={plan === option.value}
                  className={`rc-sc__opt-btn rc-sc__opt-btn--narrow ${plan === option.value ? 'rc-sc__opt-btn--on' : ''}`}
                  onClick={() => setPlan(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="rc-sc__hint" style={{ marginTop: 9 }}>
              {plan ? PLAN_BLURB[plan] : 'No plan means no access to anything — once enforcement is on.'}
            </p>
          </fieldset>

          <fieldset className="rc-sc__seg" style={{ display: 'block' }} disabled={!plan}>
            <legend>How it’s paid</legend>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', opacity: plan ? 1 : 0.5 }}>
              {[
                { key: 'trial', label: 'Free trial' },
                { key: 'paying', label: 'Paying' },
                { key: 'comped', label: 'Free of charge' },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  disabled={!plan}
                  aria-pressed={paid === option.key}
                  className={`rc-sc__opt-btn ${plan && paid === option.key ? (option.key === 'paying' ? 'rc-sc__opt-btn--ok' : 'rc-sc__opt-btn--warn') : ''}`}
                  onClick={() => setPaid(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="rc-sc__hint" style={{ marginTop: 9 }}>
              {!plan
                ? 'Not a question until the store has a plan.'
                : paid === 'trial'
                  ? `Trial ends ${trialEnd().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}.`
                  : 'Set by hand until Stripe is connected.'}
            </p>
          </fieldset>
        </div>
      </div>

      <div className="rc-sc__band">
        <div className="rc-sc__band-head">
          <h4>People</h4>
          <span className="rc-sc__band-count">the owner now, the rest once it is saved</span>
        </div>

        <div className="rc-sc__pair">
          <label className="rc-sc__field" style={{ flex: '999 1 240px' }}>
            <span>Invite the owner <span className="rc-sc__opt">optional</span></span>
            <input
              className="rc-sc__input"
              type="email"
              value={email}
              placeholder="owner@theirstore.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="rc-sc__field" style={{ flex: '1 1 160px' }}>
            <span>Role</span>
            <select className="rc-sc__select" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="owner">Owner</option>
              <option value="store_admin">Store admin</option>
              <option value="sales_admin">Team member</option>
            </select>
          </label>
        </div>
        <p className="rc-sc__hint">
          They don’t need an account — one is created when they accept. We send the email ourselves.
        </p>
      </div>

      <div className="rc-sc__tiles">
        <p className="rc-sc__note" style={{ borderStyle: 'dashed', background: 'transparent' }}>
          <span>
            <strong>Add-ons, limits and exceptions</strong><br />
            {Object.keys(ADDON_LABELS).length} add-ons open once the store is saved.
          </span>
        </p>
        <p className="rc-sc__note" style={{ borderStyle: 'dashed', background: 'transparent' }}>
          <span><strong>Chat group</strong><br />created from here once there is a team.</span>
        </p>
      </div>

      {error && <p className="rc-sc__error" role="alert">{error}</p>}

      <div className="rc-sc__savebar">
        <span>
          Nothing is saved until you create it. The store, its plan and the owner invitation are
          written together — if any part fails, none of it is kept.
        </span>
        <button type="button" className="rc-sc__btn rc-sc__btn--quiet" onClick={onCancel}>Discard</button>
        <button type="submit" className="rc-sc__btn rc-sc__btn--primary" disabled={busy || !name.trim()}>
          {busy ? 'Creating…' : 'Create store'}
        </button>
      </div>
    </form>
  );
}

export default StoreDraft;
